import React, { useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';

import useGroupGoodOption from '@ecp/common/src/hooks/good/option/useGroupGoodOption';
import useGoodOptionValidator from '@ecp/common/src/hooks/good/option/useGoodOptionValidator';
import useGoodOption from '@ecp/common/src/hooks/good/option/useGoodOption';
import { zIndex } from '@ecp/common/src/style/constant';
import { FlexBoxColumn, FlexBoxEnd } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Tooltip from '@ecp/common/src/components/tooltip/Tooltip';
import { BULKP_MAX_ORDER_COUNT } from '@ecp/common/src/const/good';

import { goodOptionListState, goodSelectedRegularDelivery } from '@mo-recoil/good/atom';
import { withOptionTotalPrice } from '@mo-recoil/good/selector';
import { useAlertDialog } from '@mo-hooks/common/useModal';
import useSiteInfo from '@mo-hooks/common/useSiteInfo';
import { QuestionIcon } from '@mo-components/styled';
import GoodCtaButtonOptionItemList from '@mo-pages/good/components/cta/option/GoodCtaButtonOptionItemList';
import GoodCtaButtonOptionSelected from '@mo-pages/good/components/cta/option/GoodCtaButtonOptionSelected';
import GoodCtaButtonOptionTotalPrice from '@mo-pages/good/components/cta/option/GoodCtaButtonOptionTotalPrice';
import GoodCtaButtonGroupItemList from '@mo-pages/good/components/cta/GoodCtaButtonGroupItemList';
import GoodOptionRegularDelivery from '@mo-pages/good/components/cta/option/GoodCtaButtonOptionRegularDelivery';
import GoodSelectedRegularDelivery from '@mo-pages/good/components/cta/option/GoodSelectedRegularDelivery';

const GoodCtaButtonOptionContainer = ({
  additionItemList,
  multiItem,
  itemNodeList,
  stockManagement,
  handleDropDownOption,
}) => {
  return (
    <>
      {multiItem && (
        <GoodCtaButtonOptionItemList
          itemNodeList={itemNodeList}
          stockManagement={stockManagement}
          onSelect={handleDropDownOption}
        />
      )}
      {additionItemList?.length > 0 && (
        <>
          <Spacing top={6} />
          <GoodCtaButtonOptionItemList
            placeholder={'추가옵션을 선택하세요.'}
            itemNodeList={additionItemList}
            stockManagement={stockManagement}
            onSelect={handleDropDownOption}
          />
        </>
      )}
    </>
  );
};

GoodCtaButtonOptionContainer.propTypes = {
  multiItem: PropTypes.bool,
  itemNodeList: PropTypes.array,
  stockManagement: PropTypes.bool,
  handleDropDownOption: PropTypes.func,
  additionItemList: PropTypes.array,
};

const GoodCtaButtonOption = ({
  isGroup,
  isRental,
  isRegularDelivery,
  subGoodList,
  regularDelivery,
  displayEndDate,
  showBtnBulkp,
  bulkpOrdPossibleMinQty,
  isPriceOpen: propIsPriceOpen, // REQ_DS_05_01
  ...goodDetail
}) => {
  const theme = useTheme();
  const { isPriceOpen: siteIsPriceOpen } = useSiteInfo();
  const isPriceOpen = propIsPriceOpen !== undefined ? propIsPriceOpen : siteIsPriceOpen;
  const [goodOptionList, setGoodOptionList] = useRecoilState(goodOptionListState);
  const totalPrice = useRecoilValue(withOptionTotalPrice);
  const [selectedRegularDelivery, setSelectedRegularDelivery] = useRecoilState(goodSelectedRegularDelivery);

  const { showAlertDialog } = useAlertDialog();

  const { optionDropdownValidator } = useGoodOptionValidator(showAlertDialog);
  const { subGoodDetail, subGoodItemList, handleGroupDropDownOption } = useGroupGoodOption({
    validator: optionDropdownValidator,
    subGoodList,
    goodOptionList,
    setGoodOptionList,
  });
  const { selectedGoodDetail, sortedSelectedOptionList, additionItemList, handleDropDownOption } = useGoodOption({
    isGroup,
    goodDetail,
    subGoodDetail,
    goodOptionList,
    setGoodOptionList,
    optionDropdownValidator,
  });

  const singleOption = useMemo(() => {
    return !isGroup && !goodDetail.multiItem;
  }, [goodDetail.multiItem, isGroup]);

  const handleChangeSelectedRegularDelivery = (updated) => {
    setSelectedRegularDelivery((prev) => ({ ...prev, ...updated }));
  };

  const BulkpOrderTooltipBox = useMemo(() => {
    return (
      <TextBox size={theme.font.size.error} color={theme.color.text.sub}>
        {
          '대량주문 최소 수량 미만일 경우 일반주문(장바구니/바로주문)으로 주문 가능합니다. 대량주문의 다중배송지는 PC에서만 가능 합니다.'
        }
      </TextBox>
    );
  });

  return (
    <Container singleOption={singleOption}>
      <FlexBoxColumn width={'100%'}>
        {isRegularDelivery && (
          <GoodOptionRegularDelivery
            regularDelivery={regularDelivery}
            selectedRegularDelivery={selectedRegularDelivery}
            displayEndDate={displayEndDate}
            handleChangeSelectedRegularDelivery={handleChangeSelectedRegularDelivery}
          />
        )}
        {isGroup && (
          <>
            <GoodCtaButtonGroupItemList
              subGoodItemList={subGoodItemList}
              subGoodDetail={subGoodDetail}
              onSelect={handleGroupDropDownOption}
            />
            <Spacing top={6} />
          </>
        )}
        <GoodCtaButtonOptionContainer
          {...selectedGoodDetail}
          additionItemList={additionItemList}
          handleDropDownOption={handleDropDownOption}
        />
      </FlexBoxColumn>
      {isRegularDelivery && (
        <GoodSelectedRegularDelivery
          hasTimeList={regularDelivery.timeList?.length > 0}
          selectedRegularDelivery={selectedRegularDelivery}
        />
      )}
      <FlexBoxColumn width={'100%'}>
        <SelectedWrapper>
          {sortedSelectedOptionList?.map((option, index) => (
            <React.Fragment key={`${option.key}__${index}`}>
              {index !== 0 && <Spacing top={6} />}
              <GoodCtaButtonOptionSelected
                option={option}
                isGroup={isGroup}
                isRental={isRental}
                isRegularDelivery={isRegularDelivery}
                stockQtyDisplay={goodDetail.stockQtyDisplay}
                showBtnBulkp={showBtnBulkp}
                selectedOptionList={goodOptionList}
                ordPossibleMaxQtyStandardCode={goodDetail?.ordPossibleMaxQtyStandardCode}
                ordPossibleMaxQty={goodDetail?.ordPossibleMaxQty}
                ordPossibleMinQty={goodDetail?.ordPossibleMinQty}
                ordPossibleMaxAmount={goodDetail?.ordPossibleMaxAmount}
                ordPossibleMaxFormattedAmount={goodDetail?.ordPossibleMaxFormattedAmount}
                ordPossibleMinAmount={goodDetail?.ordPossibleMinAmount}
                ordPossibleMinFormattedAmount={goodDetail?.ordPossibleMinFormattedAmount}
                bulkpOrdPossibleMinQty={bulkpOrdPossibleMinQty}
              />
            </React.Fragment>
          ))}
        </SelectedWrapper>
        {isPriceOpen && (
          <GoodCtaButtonOptionTotalPrice
            totalPrice={totalPrice.totalPrice}
            totalFormattedPrice={totalPrice.totalFormattedPrice}
          />
        )}
        {showBtnBulkp && (
          <FlexBoxEnd width={'100%'}>
            <TextBox>
              (대량구매 최소 수량: <TextBox color={theme.color.primary}>{bulkpOrdPossibleMinQty}개</TextBox>, 최대 수량{' '}
              {BULKP_MAX_ORDER_COUNT}개)
            </TextBox>
            <Spacing left={5} />
            <Tooltip
              value={BulkpOrderTooltipBox}
              arrow
              closeable={true}
              openWhenClicked
              placement="bottom-end"
              rootProps={{ zIndex: zIndex.popup - 1 }}
            >
              <QuestionIcon style={{ cursor: 'pointer' }} />
            </Tooltip>
          </FlexBoxEnd>
        )}
      </FlexBoxColumn>
    </Container>
  );
};

export default GoodCtaButtonOption;

GoodCtaButtonOption.propTypes = {
  multiItem: PropTypes.bool,
  itemNodeList: PropTypes.array,
  additionalItemList: PropTypes.array,
  stockManagement: PropTypes.bool,
  soldOut: PropTypes.bool,
  isGroup: PropTypes.bool,
  isRental: PropTypes.bool,
  isRegularDelivery: PropTypes.bool,
  subGoodList: PropTypes.array,
  stockQtyDisplay: PropTypes.bool,
  regularDelivery: PropTypes.shape({
    periodList: PropTypes.array,
    totalList: PropTypes.array,
    timeList: PropTypes.array,
    dayOfWeekList: PropTypes.array,
  }),
  displayEndDate: PropTypes.string,
  showBtnBulkp: PropTypes.bool,
  bulkpOrdPossibleMinQty: PropTypes.number,
  isPriceOpen: PropTypes.bool,
};

const Container = styled(FlexBoxColumn)`
  width: 100%;
  padding: 0 20px;
  min-height: ${({ singleOption }) => (singleOption ? '125px' : '254px')};
  max-height: 420px;
  overflow-y: auto;
  justify-content: space-between;
`;

const SelectedWrapper = styled(FlexBoxColumn)`
  width: 100%;
  padding: 20px 0 14px;
`;
